import { createContext, use, useCallback, useState, useSyncExternalStore } from 'react';

/**
 * Instant the current document was rendered, as epoch milliseconds.
 *
 * The root loader produces it, so it is serialized into the prerendered HTML alongside the
 * markup it produced. The hydration pass reads that same value instead of sampling the clock
 * again, which is what lets time-dependent hooks reproduce the prerendered output exactly.
 *
 * `undefined` when there is no rendered document to match (Storybook, unit tests).
 */
export const ServerNowContext = createContext<number | undefined>(undefined);

const emptySubscribe = () => () => {};

/**
 * Non-reactive timestamp: the render instant of the prerendered HTML while hydrating, then a
 * snapshot of the visitor's clock. It does not tick, but it *does* change once at hydration,
 * so never copy it into `useState` — that would pin the value to server time forever. Use
 * {@link useClock} when you need to refresh it.
 */
export const useNow = (): number => {
  const serverNow = use(ServerNowContext);
  const [clientNow] = useState(() => Date.now());

  return useSyncExternalStore(
    emptySubscribe,
    () => clientNow,
    () => serverNow ?? clientNow,
  );
};

/**
 * {@link useNow} plus a way to resample the real clock, for hooks that keep their UI current.
 *
 * The returned timestamp stays hydration-safe until `refresh` is called, and `refresh` is only
 * ever safe to call from an effect or an event — never during render.
 */
export const useClock = (): [now: number, refresh: () => void] => {
  const now = useNow();
  const [refreshed, setRefreshed] = useState<number>();
  const refresh = useCallback(() => setRefreshed(Date.now()), []);

  return [refreshed ?? now, refresh];
};
